import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { Cinema } from '../models';
import { API_BASE } from './api';

export type LocationMode = 'all' | 'near' | 'manual';

export interface LocationFilterState {
  mode: LocationMode;
  radiusKm: number;
  latitude?: number;
  longitude?: number;
  label?: string;
  loading: boolean;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LocationFilterService {
  private readonly cinemaById = new Map<string, Cinema>();
  private readonly stateSubject = new BehaviorSubject<LocationFilterState>({
    mode: 'all',
    radiusKm: 25,
    loading: false,
  });

  readonly state$ = this.stateSubject.asObservable();

  constructor(private readonly http: HttpClient) {
    this.loadCinemaCoordinates();
  }

  get state(): LocationFilterState {
    return this.stateSubject.value;
  }

  useAllIreland() {
    this.stateSubject.next({
      ...this.state,
      mode: 'all',
      loading: false,
      error: undefined,
      label: undefined,
    });
  }

  useNearMe() {
    if (!navigator.geolocation) {
      this.stateSubject.next({
        ...this.state,
        mode: 'near',
        loading: false,
        error: 'Location is not available in this browser.',
      });
      return;
    }

    this.stateSubject.next({
      ...this.state,
      mode: 'near',
      loading: true,
      error: undefined,
      latitude: undefined,
      longitude: undefined,
    });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.stateSubject.next({
          ...this.state,
          mode: 'near',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          label: undefined,
          loading: false,
          error: undefined,
        });
      },
      (error) => {
        this.stateSubject.next({
          ...this.state,
          mode: 'near',
          loading: false,
          error: this.locationErrorMessage(error),
        });
      },
      {
        enableHighAccuracy: false,
        maximumAge: 0,
        timeout: 12000,
      },
    );
  }

  useManualLocation(label: string, latitude: number, longitude: number) {
    this.stateSubject.next({
      ...this.state,
      mode: 'manual',
      latitude,
      longitude,
      label,
      loading: false,
      error: undefined,
    });
  }

  setRadius(radiusKm: number) {
    const radius = Math.max(1, Math.min(500, Math.round(radiusKm)));
    this.stateSubject.next({
      ...this.state,
      radiusKm: radius,
      error: undefined,
    });
  }

  isNearActive(): boolean {
    return this.state.mode !== 'all' && this.hasPosition();
  }

  hasPosition(): boolean {
    return typeof this.state.latitude === 'number' && typeof this.state.longitude === 'number';
  }

  distanceToCinema(cinema?: Cinema): number | undefined {
    cinema = this.withKnownCoordinates(cinema);
    if (!cinema || !this.hasPosition() || typeof cinema.latitude !== 'number' || typeof cinema.longitude !== 'number') {
      return undefined;
    }

    return this.distanceKm(this.state.latitude!, this.state.longitude!, cinema.latitude, cinema.longitude);
  }

  isCinemaWithinRadius(cinema?: Cinema): boolean {
    if (this.state.mode === 'all') return true;
    cinema = this.withKnownCoordinates(cinema);
    const distance = this.distanceToCinema(cinema);
    return typeof distance === 'number' && distance <= this.state.radiusKm;
  }

  filterCinemas<T extends { cinema?: Cinema }>(rows: T[]): T[];
  filterCinemas<T extends Cinema>(rows: T[]): T[];
  filterCinemas<T extends Cinema | { cinema?: Cinema }>(rows: T[]): T[] {
    if (this.state.mode === 'all') return rows;
    return rows.filter((row) => {
      const cinema = this.isCinema(row) ? row : row.cinema;
      return this.isCinemaWithinRadius(cinema);
    });
  }

  sortCinemasByDistance<T extends Cinema>(rows: T[]): T[] {
    if (!this.isNearActive()) return rows;
    return [...rows].sort((a, b) => (this.distanceToCinema(a) ?? Number.MAX_VALUE) - (this.distanceToCinema(b) ?? Number.MAX_VALUE));
  }

  formatDistance(cinema?: Cinema): string {
    cinema = this.withKnownCoordinates(cinema);
    const distance = this.distanceToCinema(cinema);
    if (typeof distance !== 'number') return '';
    if (distance < 10) return `${distance.toFixed(1)} km`;
    return `${Math.round(distance)} km`;
  }

  private distanceKm(fromLat: number, fromLng: number, toLat: number, toLng: number): number {
    const earthRadiusKm = 6371;
    const dLat = this.toRadians(toLat - fromLat);
    const dLng = this.toRadians(toLng - fromLng);
    const lat1 = this.toRadians(fromLat);
    const lat2 = this.toRadians(toLat);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  private toRadians(value: number): number {
    return value * Math.PI / 180;
  }

  private loadCinemaCoordinates() {
    this.http.get<Cinema[]>(`${API_BASE}/cinemas`).subscribe({
      next: (cinemas) => {
        cinemas.forEach((cinema) => this.cinemaById.set(cinema.id, cinema));
        this.stateSubject.next({ ...this.state });
      },
      error: () => undefined,
    });
  }

  private withKnownCoordinates(cinema?: Cinema): Cinema | undefined {
    if (!cinema) return undefined;
    if (typeof cinema.latitude === 'number' && typeof cinema.longitude === 'number') return cinema;
    return this.cinemaById.get(cinema.id) ?? cinema;
  }

  private isCinema(value: Cinema | { cinema?: Cinema }): value is Cinema {
    return 'id' in value && 'name' in value && 'chain' in value;
  }

  private locationErrorMessage(error: GeolocationPositionError): string {
    if (error.code === error.PERMISSION_DENIED) return 'Location permission was blocked.';
    if (error.code === error.POSITION_UNAVAILABLE) return 'Could not detect your current location.';
    if (error.code === error.TIMEOUT) return 'Location lookup timed out.';
    return 'Could not detect your current location.';
  }
}
